import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CONTACTS, formatMessageTime } from './chatData';
import EmojiPicker from './EmojiPicker';

function ChatWindow({ contact, conversation, currentUser, onSend }) {
  const [input, setInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages, conversation.typing, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [contact?.id]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim(), replyTo);
    setInput('');
    setReplyTo(null);
    setShowEmoji(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getSenderInfo = (senderId) => {
    if (senderId === currentUser.id) return currentUser;
    return CONTACTS.find((c) => c.id === senderId) || { name: 'Unknown', avatar: '👤' };
  };

  const getMessageById = (id) => {
    return conversation.messages.find((m) => m.id === id);
  };

  const renderStatus = (status) => {
    switch (status) {
      case 'sent':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        );
      case 'delivered':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="18 6 7 17 2 12" />
            <polyline points="24 6 13 17" />
          </svg>
        );
      case 'read':
        return (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5">
            <polyline points="18 6 7 17 2 12" />
            <polyline points="24 6 13 17" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Group messages by date
  const groupedMessages = [];
  let lastDate = '';
  conversation.messages.forEach((msg) => {
    const date = new Date(msg.timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    if (date !== lastDate) {
      groupedMessages.push({ type: 'date', date });
      lastDate = date;
    }
    groupedMessages.push({ type: 'message', ...msg });
  });

  if (!contact) return <div className="chat-window chat-empty">Select a conversation</div>;

  return (
    <div className="chat-window">
      {/* Chat Header */}
      <div className="chat-window-header">
        <div className="chat-window-user">
          <div className="chat-window-avatar">
            <span>{contact.avatar}</span>
            <span className={`chat-status-dot ${contact.status}`}></span>
          </div>
          <div className="chat-window-user-info">
            <span className="chat-window-name">{contact.name}</span>
            <span className="chat-window-status">
              {conversation.typing ? (
                <span className="chat-typing-indicator">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  typing...
                </span>
              ) : (
                contact.status === 'online' ? 'Online' :
                contact.status === 'away' ? 'Away' : 'Offline'
              )}
            </span>
          </div>
        </div>
        <div className="chat-window-actions">
          <button className="chat-header-btn" title="Voice call">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
            </svg>
          </button>
          <button className="chat-header-btn" title="Video call">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
          </button>
          <button className="chat-header-btn" title="Info">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {groupedMessages.map((item, idx) => {
          if (item.type === 'date') {
            return (
              <div key={`date-${idx}`} className="chat-date-divider">
                <span>{item.date}</span>
              </div>
            );
          }

          const sender = getSenderInfo(item.senderId);
          const isMe = item.senderId === currentUser.id;
          const repliedMsg = item.replyTo ? getMessageById(item.replyTo) : null;

          return (
            <div key={item.id} className={`chat-message ${isMe ? 'sent' : 'received'}`}>
              {!isMe && contact.isGroup && (
                <span className="chat-msg-sender-name" style={{ color: 'var(--accent)' }}>
                  {sender.name}
                </span>
              )}
              {repliedMsg && (
                <div className="chat-reply-preview">
                  <span className="chat-reply-author">
                    {getSenderInfo(repliedMsg.senderId).name}
                  </span>
                  <span className="chat-reply-text">
                    {repliedMsg.text.length > 60 ? repliedMsg.text.slice(0, 60) + '...' : repliedMsg.text}
                  </span>
                </div>
              )}
              <div className="chat-msg-bubble">
                <p className="chat-msg-text">{item.text}</p>
                <div className="chat-msg-meta">
                  <span className="chat-msg-time">{formatMessageTime(item.timestamp)}</span>
                  {isMe && <span className="chat-msg-status">{renderStatus(item.status)}</span>}
                </div>
              </div>
              <button
                className="chat-reply-btn"
                onClick={() => { setReplyTo(item.id); inputRef.current?.focus(); }}
                title="Reply"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 00-4-4H4" />
                </svg>
              </button>
            </div>
          );
        })}

        {conversation.typing && (
          <div className="chat-message received">
            <div className="chat-msg-bubble typing-bubble">
              <div className="chat-typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyTo && (() => {
        const repliedMsg = getMessageById(replyTo);
        if (!repliedMsg) return null;
        const sender = getSenderInfo(repliedMsg.senderId);
        return (
          <div className="chat-reply-bar">
            <div className="chat-reply-bar-content">
              <span className="chat-reply-bar-author">{sender.name}</span>
              <span className="chat-reply-bar-text">
                {repliedMsg.text.length > 80 ? repliedMsg.text.slice(0, 80) + '...' : repliedMsg.text}
              </span>
            </div>
            <button className="chat-reply-bar-close" onClick={() => setReplyTo(null)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        );
      })()}

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="chat-input-container">
          <button
            className="chat-input-btn emoji-btn"
            onClick={() => setShowEmoji(!showEmoji)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
              <line x1="9" y1="9" x2="9.01" y2="9" />
              <line x1="15" y1="9" x2="15.01" y2="9" />
            </svg>
          </button>

          {showEmoji && (
            <EmojiPicker
              onSelect={(emoji) => {
                setInput((prev) => prev + emoji);
                inputRef.current?.focus();
              }}
              onClose={() => setShowEmoji(false)}
            />
          )}

          <textarea
            ref={inputRef}
            className="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
          />

          <button className="chat-input-btn attach-btn" title="Attach file">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
          </button>

          <button
            className={`chat-send-btn ${input.trim() ? 'active' : ''}`}
            onClick={handleSend}
            disabled={!input.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;
